/**
 * AuthZEN Permission Preloading
 *
 * Preloads permissions at app/plugin startup so UI components render without
 * spinners. Issues a single batch request, caches every decision, and
 * exposes a React boundary component for declarative use.
 *
 * IMPORTANT — pdp_application routing:
 * The PDP selects a policy set based on context.pdp_application. Resources
 * that belong to a plugin (e.g. "entitlement-ledger") MUST be routed to
 * that plugin's policy set, not to "experience". The helper
 * `inferPdpApplication` extracts the correct value from the resource name.
 */

import { useState, useEffect } from 'react';
import { getValidatedSubject } from './authz-subject';
import { CorrelationContext } from './authz-correlation';
import { AuthZENResponseValidator } from './authz-validator';
import { ResourceBuilder } from './authz-resource';
import { PDPDiscovery } from './authz-discovery';
import { AuthZENCache } from './authz-cache';
import { buildAuthZENAction } from './authz-operation-refs';
import { inferPdpApplication } from './authz-constants';

export interface PermissionSpec {
	resource: string;
	actions: string[];
	resourceId?: string;
}

export interface PreloadResult {
	totalPermissions: number;
	preloadedCount: number;
	duration: number;
	cacheHitRate: number;
}

export class AuthZENPreloader {
	private static isPreloading = false;
	private static preloadPromise: Promise<PreloadResult> | null = null;

	/**
	 * Preload permissions via a single AuthZEN batch request.
	 *
	 * pdp_application is inferred per-resource so plugin resources are
	 * routed to the correct policy set automatically.
	 */
	static async preloadCommonPermissions(
		permissions: PermissionSpec[],
		options?: {
			batchSize?: number;
			debug?: boolean;
		}
	): Promise<PreloadResult> {
		if (this.isPreloading && this.preloadPromise) {
			return this.preloadPromise;
		}

		this.isPreloading = true;

		this.preloadPromise = (async () => {
			const startTime = performance.now();
			const debug = options?.debug ?? false;

			try {
				const subject = await getValidatedSubject();

				const evaluations = permissions.flatMap(perm =>
					perm.actions.map(action => {
						const pdpApp = inferPdpApplication(perm.resource);
						const resource = ResourceBuilder.build(perm.resource, perm.resourceId);
						return {
							resource: {
								...resource,
								properties: { ...(resource.properties || {}), pdp_application: pdpApp },
							},
							action: buildAuthZENAction(perm.resource, action),
							context: {
								pdp_application: pdpApp,
								decision_intent: 'cache_warmup_probe',
								decision_surface: 'preload',
								decision_origin: 'experience_frontend',
								decision_is_background: true,
							}
						};
					})
				);

				const totalPermissions = evaluations.length;

				if (debug) {
					console.info(`[AuthZ Preload] Preloading ${totalPermissions} permissions`, { permissions });
				}

				const endpoint = await PDPDiscovery.getBatchEndpoint();
				const requestId = CorrelationContext.createCorrelation('preload-permissions');

				const requestBody = {
					evaluations: evaluations.map(e => ({
						subject,
						resource: e.resource,
						action: e.action,
						context: e.context
					})),
					options: { evaluations_semantic: 'execute_all' }
				};

				const csrf = (document.cookie.match(/(?:^|; )_eid_csrf_v1=([^;]+)/)?.[1]) || '';

				const response = await fetch(endpoint, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Request-ID': requestId,
						'X-CSRF-Token': csrf,
						'X-Requested-With': 'XMLHttpRequest'
					},
					credentials: 'include',
					body: JSON.stringify(requestBody)
				});

				if (!response.ok) {
					throw new Error(`Preload batch request failed: HTTP ${response.status}`);
				}

				const data = await response.json();
				const validated = AuthZENResponseValidator.validateBatchResponse(data);

				let cachedCount = 0;
				validated.evaluations.forEach((decision, index) => {
					const evaluation = evaluations[index];
					AuthZENCache.set(
						subject,
						evaluation.resource,
						evaluation.action,
						decision.decision,
						decision.context,
						decision.context?.obligations,
						requestId
					);
					cachedCount++;
				});

				const duration = performance.now() - startTime;
				const cacheStats = AuthZENCache.getStats();

				const result: PreloadResult = {
					totalPermissions,
					preloadedCount: cachedCount,
					duration,
					cacheHitRate: cacheStats.hitRate
				};

				if (debug) {
					console.info(
						`[AuthZ Preload] Preloaded ${cachedCount}/${totalPermissions} in ${duration.toFixed(0)}ms`,
					);
				}

				return result;

			} catch (error) {
				console.error('[AuthZ Preload] Preload failed:', error);
				return {
					totalPermissions: 0,
					preloadedCount: 0,
					duration: performance.now() - startTime,
					cacheHitRate: 0
				};

			} finally {
				this.isPreloading = false;
				this.preloadPromise = null;
			}
		})();

		return this.preloadPromise;
	}

	static async preloadForPlugin(
		pluginId: string,
		resources: PermissionSpec[]
	): Promise<PreloadResult> {
		return this.preloadCommonPermissions(resources, { debug: true });
	}

	/**
	 * Preload permissions based on user roles.
	 * Delegates to preloadCommonPermissions with role-appropriate specs.
	 */
	static async preloadForRole(user: {
		id: string;
		role?: string;
		roles?: string[];
	}): Promise<PreloadResult> {
		const userRoles = user.roles || (user.role ? [user.role] : []);

		const rolePermissions: Record<string, PermissionSpec[]> = {
			admin: [
				{ resource: 'dashboard', actions: ['view'] },
				{ resource: 'profile', actions: ['view', 'edit'] },
				{ resource: 'navigation', actions: ['view'] },
			],
			user: [
				{ resource: 'dashboard', actions: ['view'] },
				{ resource: 'profile', actions: ['view', 'edit'] },
				{ resource: 'navigation', actions: ['view'] },
			],
		};

		const permissionsToPreload: PermissionSpec[] = [];
		const seen = new Set<string>();

		for (const role of userRoles) {
			for (const perm of (rolePermissions[role.toLowerCase()] || [])) {
				const key = `${perm.resource}:${perm.actions.join(',')}`;
				if (!seen.has(key)) {
					seen.add(key);
					permissionsToPreload.push(perm);
				}
			}
		}

		if (permissionsToPreload.length === 0) {
			return this.warmupCache();
		}

		return this.preloadCommonPermissions(permissionsToPreload);
	}

	/**
	 * Warm up cache with critical permissions every user needs.
	 */
	static async warmupCache(): Promise<PreloadResult> {
		return this.preloadCommonPermissions([
			{ resource: 'dashboard', actions: ['view'] },
			{ resource: 'profile', actions: ['view', 'edit'] },
			{ resource: 'navigation', actions: ['view'] },
		]);
	}

	static getPreloadStatus(): boolean {
		return this.isPreloading;
	}
}

/**
 * React hook for permission preloading at app/component mount
 * 
 * @param permissions - Permissions to preload
 * @param options - Hook options
 * @returns {object} { ready, preloading, result }
 * 
 * @example
 * ```typescript
 * function UserManagementPage() {
 *   const { ready, preloading } = usePermissionPreload([
 *     { resource: 'user', actions: ['view', 'edit', 'delete'] }
 *   ]);
 *   
 *   if (!ready) return <LoadingScreen />;
 *   
 *   return <UserList />;  // ✅ Renders instantly (all permissions cached)
 * }
 * ```
 */
export function usePermissionPreload(
	permissions: PermissionSpec[],
	options?: {
		/** Enable preloading (default: true) */
		enabled?: boolean;
		/** Preload on mount (default: true) */
		preloadOnMount?: boolean;
	}
) {
	const [ready, setReady] = useState(false);
	const [preloading, setPreloading] = useState(false);
	const [result, setResult] = useState<PreloadResult | null>(null);
	const [error, setError] = useState<Error | null>(null);
	
	const enabled = options?.enabled ?? true;
	const preloadOnMount = options?.preloadOnMount ?? true;
	
	useEffect(() => {
		if (!enabled || !preloadOnMount) {
			setReady(true);
			return;
		}
		
		let mounted = true;
		
		setPreloading(true);
		
		AuthZENPreloader.preloadCommonPermissions(permissions)
			.then(res => {
				if (mounted) {
					setResult(res);
					setReady(true);
					setError(null);
				}
			})
			.catch(err => {
				if (mounted) {
					console.error('[AuthZ Preload] Hook preload failed:', err);
					setError(err);
					setReady(true);  // Continue anyway
				}
			})
			.finally(() => {
				if (mounted) {
					setPreloading(false);
				}
			});
		
		return () => {
			mounted = false;
		};
	}, [enabled, preloadOnMount, permissions]);
	
	return {
		ready,
		preloading,
		result,
		error
	} as const;
}

/**
 * React component wrapper for permission preloading
 * 
 * Shows loading screen while preloading permissions,
 * then renders children with all permissions cached.
 * 
 * @example
 * ```tsx
 * <PermissionPreloadBoundary
 *   permissions={[
 *     { resource: 'user', actions: ['view', 'edit'] }
 *   ]}
 *   fallback={<SplashScreen />}
 * >
 *   <UserManagementApp />
 * </PermissionPreloadBoundary>
 * ```
 */
export function PermissionPreloadBoundary({
	permissions,
	children,
	fallback
}: {
	permissions: PermissionSpec[];
	children: React.ReactNode;
	fallback?: React.ReactNode;
}) {
	const { ready, preloading } = usePermissionPreload(permissions);
	
	if (preloading || !ready) {
		return fallback || null;
	}
	
	return children as any;
}

